import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { Button, Dropdown, Label, Switch } from '@heroui/react';
import { Segment, Sheet } from '@heroui-pro/react';
import type { CategoryAttributeRole } from '@/api/models';
import { PATH } from '@/router/PATH';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { CATEGORY_ATTRIBUTE_ROLES } from './categoryAttributesMeta';
import { useCategoryAttributesSheet } from './useCategoryAttributesSheet';

type CategoryAttributesSheetProps = {
    categoryId: string | null;
    categoryName: string;
    categorySlug: string;
    isOpen: boolean;
    onClose: () => void;
};

/**
 * Left-side drawer to manage which dictionary attributes a category uses
 * (Surface A): add/remove, set each one's role (Variation / Descriptive),
 * toggle required, and reorder. Edits are local until **Save**, which sends one
 * full-replace PUT; editing an attribute's value *list* stays on the global
 * dictionary (`/attributes`), reachable via the "new attribute" link.
 */
export const CategoryAttributesSheet: React.FC<
    CategoryAttributesSheetProps
> = ({ categoryId, categoryName, categorySlug, isOpen, onClose }) => {
    const { t } = useTranslation('pages');
    const sheet = useCategoryAttributesSheet({ categoryId, onSaved: onClose });

    const copy = React.useMemo(
        () => ({
            subtitle: t('categories.components.categoryAttributes.subtitle'),
            addAttribute: t(
                'categories.components.categoryAttributes.addAttribute',
            ),
            newType: t('categories.components.categoryAttributes.newType'),
            required: t('categories.components.categoryAttributes.required'),
            remove: t('categories.components.categoryAttributes.remove'),
            moveUp: t('categories.components.categoryAttributes.moveUp'),
            moveDown: t('categories.components.categoryAttributes.moveDown'),
            empty: t('categories.components.categoryAttributes.empty'),
            allAdded: t('categories.components.categoryAttributes.allAdded'),
            loading: t('categories.components.categoryAttributes.loading'),
            errorMessage: t(
                'categories.components.categoryAttributes.errorMessage',
            ),
            errorRetry: t(
                'categories.components.categoryAttributes.errorRetry',
            ),
            cancel: t('categories.components.categoryAttributes.cancel'),
            save: t('categories.components.categoryAttributes.save'),
            roles: CATEGORY_ATTRIBUTE_ROLES.map((role) => ({
                id: role.id,
                label: t(role.labelKey),
            })),
        }),
        [t],
    );

    return (
        <Sheet
            isOpen={isOpen}
            onOpenChange={(open) => !open && onClose()}
            placement="left"
        >
            <Sheet.Backdrop>
                <Sheet.Content className="w-full sm:max-w-[480px]">
                    <Sheet.Dialog
                        aria-label={categoryName || copy.subtitle}
                        {...getNodeTestIdProp('category-attributes-sheet')}
                    >
                        <Sheet.CloseTrigger />
                        <Sheet.Header
                            {...getNodeTestIdProp(
                                'category-attributes-sheet-header',
                            )}
                        >
                            <Sheet.Heading
                                {...getNodeTestIdProp(
                                    'category-attributes-sheet-title',
                                )}
                            >
                                {categoryName}
                            </Sheet.Heading>
                            <p className="font-mono text-xs text-muted">
                                {categorySlug} · {copy.subtitle}
                            </p>
                        </Sheet.Header>
                        <Sheet.Body
                            className="flex flex-1 flex-col gap-4"
                            {...getNodeTestIdProp(
                                'category-attributes-sheet-body',
                            )}
                        >
                            {sheet.loadError ? (
                                <div
                                    className="flex flex-col items-center gap-3 py-10 text-muted"
                                    {...getNodeTestIdProp(
                                        'category-attributes-error',
                                    )}
                                >
                                    <Icon
                                        aria-hidden
                                        className="size-7 text-danger"
                                        icon="gravity-ui:circle-exclamation"
                                    />
                                    <span className="text-sm">
                                        {copy.errorMessage}
                                    </span>
                                    <Button
                                        onPress={sheet.onRetry}
                                        size="sm"
                                        variant="secondary"
                                        {...getNodeTestIdProp(
                                            'category-attributes-error-retry',
                                        )}
                                    >
                                        {copy.errorRetry}
                                    </Button>
                                </div>
                            ) : sheet.isLoading ? (
                                <div
                                    className="flex flex-col items-center gap-2 py-10 text-muted"
                                    {...getNodeTestIdProp(
                                        'category-attributes-loading',
                                    )}
                                >
                                    <Icon
                                        aria-hidden
                                        className="size-7 animate-spin text-default-500"
                                        icon="gravity-ui:arrows-rotate-right"
                                    />
                                    <span className="text-sm">
                                        {copy.loading}
                                    </span>
                                </div>
                            ) : (
                                <>
                                    <div className="flex flex-col gap-2">
                                        <Dropdown>
                                            <Button
                                                fullWidth
                                                isDisabled={
                                                    sheet.availableTypes
                                                        .length === 0
                                                }
                                                variant="secondary"
                                                {...getNodeTestIdProp(
                                                    'category-attributes-add',
                                                )}
                                            >
                                                <Icon icon="gravity-ui:plus" />
                                                {sheet.availableTypes.length ===
                                                0
                                                    ? copy.allAdded
                                                    : copy.addAttribute}
                                            </Button>
                                            <Dropdown.Popover className="max-h-[320px] min-w-[260px] overflow-y-auto">
                                                <Dropdown.Menu
                                                    onAction={(key) =>
                                                        sheet.onAddType(
                                                            key as string,
                                                        )
                                                    }
                                                >
                                                    {sheet.availableTypes.map(
                                                        (type) => (
                                                            <Dropdown.Item
                                                                id={type.id}
                                                                key={type.id}
                                                                textValue={
                                                                    type.name
                                                                }
                                                                {...getNodeTestIdProp(
                                                                    `category-attributes-add-option-${type.id}`,
                                                                )}
                                                            >
                                                                <Label>
                                                                    {type.name}
                                                                </Label>
                                                            </Dropdown.Item>
                                                        ),
                                                    )}
                                                </Dropdown.Menu>
                                            </Dropdown.Popover>
                                        </Dropdown>
                                        <Link
                                            className="inline-flex items-center gap-1 self-start text-xs text-muted hover:text-foreground"
                                            to={PATH.ATTRIBUTES}
                                            {...getNodeTestIdProp(
                                                'category-attributes-new-type',
                                            )}
                                        >
                                            <Icon
                                                aria-hidden
                                                className="size-3.5"
                                                icon="gravity-ui:arrow-up-right-from-square"
                                            />
                                            {copy.newType}
                                        </Link>
                                    </div>
                                    {sheet.draft.length === 0 ? (
                                        <div
                                            className="flex flex-1 flex-col items-center justify-center gap-2 py-10 text-muted"
                                            {...getNodeTestIdProp(
                                                'category-attributes-empty',
                                            )}
                                        >
                                            <Icon
                                                aria-hidden
                                                className="size-7 text-default-500"
                                                icon="gravity-ui:tag"
                                            />
                                            <span className="text-center text-sm">
                                                {copy.empty}
                                            </span>
                                        </div>
                                    ) : (
                                        <ul
                                            className="flex flex-col gap-3"
                                            {...getNodeTestIdProp(
                                                'category-attributes-list',
                                            )}
                                        >
                                            {sheet.draft.map((item, index) => (
                                                <li
                                                    className="flex flex-col gap-3 rounded-xl border border-border bg-surface-secondary p-3"
                                                    key={item.attributeTypeId}
                                                    {...getNodeTestIdProp(
                                                        `category-attribute-row-${item.attributeTypeId}`,
                                                    )}
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex flex-col">
                                                            <Button
                                                                aria-label={
                                                                    copy.moveUp
                                                                }
                                                                isDisabled={
                                                                    index === 0
                                                                }
                                                                isIconOnly
                                                                onPress={() =>
                                                                    sheet.onMove(
                                                                        item.attributeTypeId,
                                                                        'up',
                                                                    )
                                                                }
                                                                size="sm"
                                                                variant="tertiary"
                                                                {...getNodeTestIdProp(
                                                                    `category-attribute-move-up-${item.attributeTypeId}`,
                                                                )}
                                                            >
                                                                <Icon icon="gravity-ui:chevron-up" />
                                                            </Button>
                                                            <Button
                                                                aria-label={
                                                                    copy.moveDown
                                                                }
                                                                isDisabled={
                                                                    index ===
                                                                    sheet.draft
                                                                        .length -
                                                                        1
                                                                }
                                                                isIconOnly
                                                                onPress={() =>
                                                                    sheet.onMove(
                                                                        item.attributeTypeId,
                                                                        'down',
                                                                    )
                                                                }
                                                                size="sm"
                                                                variant="tertiary"
                                                                {...getNodeTestIdProp(
                                                                    `category-attribute-move-down-${item.attributeTypeId}`,
                                                                )}
                                                            >
                                                                <Icon icon="gravity-ui:chevron-down" />
                                                            </Button>
                                                        </div>
                                                        <div className="flex min-w-0 flex-1 flex-col">
                                                            <span className="text-sm font-medium text-foreground">
                                                                {
                                                                    item
                                                                        .attributeType
                                                                        .name
                                                                }
                                                            </span>
                                                            <span className="font-mono text-xs text-muted">
                                                                {
                                                                    item
                                                                        .attributeType
                                                                        .slug
                                                                }
                                                            </span>
                                                        </div>
                                                        <Button
                                                            aria-label={
                                                                copy.remove
                                                            }
                                                            isIconOnly
                                                            onPress={() =>
                                                                sheet.onRemoveType(
                                                                    item.attributeTypeId,
                                                                )
                                                            }
                                                            size="sm"
                                                            variant="danger-soft"
                                                            {...getNodeTestIdProp(
                                                                `category-attribute-remove-${item.attributeTypeId}`,
                                                            )}
                                                        >
                                                            <Icon icon="gravity-ui:trash-bin" />
                                                        </Button>
                                                    </div>
                                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                                        <Segment
                                                            aria-label={
                                                                copy.subtitle
                                                            }
                                                            onSelectionChange={(
                                                                key,
                                                            ) =>
                                                                sheet.onRoleChange(
                                                                    item.attributeTypeId,
                                                                    key as CategoryAttributeRole,
                                                                )
                                                            }
                                                            selectedKey={
                                                                item.role
                                                            }
                                                            size="sm"
                                                            variant="ghost"
                                                            {...getNodeTestIdProp(
                                                                `category-attribute-role-${item.attributeTypeId}`,
                                                            )}
                                                        >
                                                            {copy.roles.map(
                                                                (role) => (
                                                                    <Segment.Item
                                                                        id={
                                                                            role.id
                                                                        }
                                                                        key={
                                                                            role.id
                                                                        }
                                                                        {...getNodeTestIdProp(
                                                                            `category-attribute-role-${item.attributeTypeId}-${role.id}`,
                                                                        )}
                                                                    >
                                                                        <Segment.Separator />
                                                                        {
                                                                            role.label
                                                                        }
                                                                    </Segment.Item>
                                                                ),
                                                            )}
                                                        </Segment>
                                                        <Switch
                                                            isSelected={
                                                                item.isRequired
                                                            }
                                                            onChange={(value) =>
                                                                sheet.onRequiredChange(
                                                                    item.attributeTypeId,
                                                                    value,
                                                                )
                                                            }
                                                            {...getNodeTestIdProp(
                                                                `category-attribute-required-${item.attributeTypeId}`,
                                                            )}
                                                        >
                                                            <Switch.Control>
                                                                <Switch.Thumb />
                                                            </Switch.Control>
                                                            <Switch.Content>
                                                                <Label className="text-xs text-muted">
                                                                    {
                                                                        copy.required
                                                                    }
                                                                </Label>
                                                            </Switch.Content>
                                                        </Switch>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {sheet.saveError ? (
                                        <p
                                            className="text-sm text-danger"
                                            {...getNodeTestIdProp(
                                                'category-attributes-save-error',
                                            )}
                                        >
                                            {sheet.saveError}
                                        </p>
                                    ) : null}
                                </>
                            )}
                        </Sheet.Body>
                        <Sheet.Footer
                            {...getNodeTestIdProp(
                                'category-attributes-sheet-footer',
                            )}
                        >
                            <Button
                                isDisabled={sheet.isSaving}
                                onPress={onClose}
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    'category-attributes-cancel',
                                )}
                            >
                                {copy.cancel}
                            </Button>
                            <Button
                                isDisabled={!sheet.isDirty}
                                isPending={sheet.isSaving}
                                onPress={sheet.onSave}
                                variant="primary"
                                {...getNodeTestIdProp(
                                    'category-attributes-save',
                                )}
                            >
                                {copy.save}
                            </Button>
                        </Sheet.Footer>
                    </Sheet.Dialog>
                </Sheet.Content>
            </Sheet.Backdrop>
        </Sheet>
    );
};
